import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import {
  ActionBar,
  DataGrid,
  DetailPane,
  SplitView,
  StatusBar,
  type DesktopAction,
  type DetailTab,
  type GridColumn,
} from '@/components/desktop';
import { StatePill } from '@/components/hr/StatePill';
import { printPayslips } from '@/components/hrPrint';
import { ErrorNote, money, shortDate } from '@/components/ui';
import { hrGet, periodLabel } from '@/hooks/hr';
import { LinesTable, NetPay } from './PayslipView';
import { dayCount } from './payrollShared';
import './payroll.css';

interface MyPayslipRow {
  id: string;
  documentNo: string;
  title: string | null;
  kind: 'REGULAR' | 'OFF_CYCLE';
  periodStart: string | null;
  periodEnd: string | null;
  grossPay: string;
  deductionsTotal: string;
  netPay: string;
  paidOn: string | null;
}

interface MyPayslipList {
  employee: { code: string; nameEn: string; nameUr: string | null };
  items: MyPayslipRow[];
}

interface MyPayslip {
  id: string;
  documentNo: string;
  title: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  employeeCode: string;
  employeeName: string;
  employeeNameUr: string | null;
  designationName: string | null;
  farm: string;
  payableDays: string;
  absentDays: string;
  overtimeHours: string;
  grossPay: string;
  deductionsTotal: string;
  netPay: string;
  paidOn: string | null;
  paymentMethod?: 'CASH' | 'BANK' | 'WALLET';
  paidMethod?: 'CASH' | 'BANK' | 'WALLET' | null;
  lines: Array<{ label: string; kind: 'EARNING' | 'DEDUCTION'; quantity: string | null; rupees: string }>;
}

interface MySummary {
  year: number;
  owed: Array<{ documentNo: string; kind: 'ADVANCE' | 'LOAN'; eachMonthRupees: string; owedRupees: string }>;
  leave: Array<{ leaveTypeId: string; nameEn: string; isPaid: boolean; remaining: string; pending: string }>;
}

function notLinked(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

function periodText(p: { periodStart: string | null; periodEnd: string | null }): string {
  return p.periodStart && p.periodEnd ? periodLabel(p.periodStart, p.periodEnd) : '—';
}

function printMine(s: MyPayslip) {
  printPayslips(
    [
      {
        documentNo: s.documentNo,
        runTitle: s.title,
        periodStart: s.periodStart,
        periodEnd: s.periodEnd,
        employeeCode: s.employeeCode,
        employeeName: s.employeeName,
        employeeNameUr: s.employeeNameUr,
        designationName: s.designationName,
        farm: s.farm,
        paymentMethod: s.paidMethod ?? s.paymentMethod ?? 'CASH',
        payableDays: s.payableDays,
        absentDays: s.absentDays,
        overtimeHours: s.overtimeHours,
        grossSalary: s.grossPay,
        totalDeductions: s.deductionsTotal,
        netPayable: s.netPay,
        lines: s.lines.map((l) => ({ label: l.label, kind: l.kind, quantity: l.quantity, payAmount: l.rupees })),
        paidOn: s.paidOn,
      },
    ],
    'A5',
  );
}

/**
 * My Pay: the signed-in person's own posted payslips, to read and print.
 * Works only when HR has linked the sign-in to an employee record.
 */
export function MyPay() {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [tab, setTab] = useState('payslip');

  const list = useQuery({
    queryKey: ['hr', 'me', 'payslips'],
    queryFn: () => hrGet<MyPayslipList>('/api/v1/hr/me/payslips'),
    retry: (count, error) => !notLinked(error) && count < 2,
  });
  const summary = useQuery({
    queryKey: ['hr', 'me', 'summary'],
    queryFn: () => hrGet<MySummary>('/api/v1/hr/me/summary'),
    retry: false,
  });
  const detail = useQuery({
    queryKey: ['hr', 'me', 'payslips', selectedKey],
    queryFn: () => hrGet<MyPayslip>(`/api/v1/hr/me/payslips/${selectedKey}`),
    enabled: selectedKey !== null,
  });

  if (notLinked(list.error)) {
    return (
      <section className="panel mypay__notlinked" aria-label="My pay">
        <h2 className="withicon">
          <AppIcon kind="payslip" />
          My pay
        </h2>
        <p>
          <strong>Your sign-in is not linked to an employee record. Ask HR to link it.</strong>
        </p>
        <p className="muted">
          Once HR links your sign-in to your employee record, your payslips will show here and you can print them.
        </p>
      </section>
    );
  }

  const rows = list.data?.items ?? [];
  const me = list.data?.employee;
  const selected = rows.find((r) => r.id === selectedKey) ?? null;
  const d = detail.data && detail.data.id === selectedKey ? detail.data : null;

  const actions: DesktopAction[] = [
    {
      label: 'Print payslip',
      icon: 'print',
      onSelect: () => {
        if (d) printMine(d);
      },
      enabled: d !== null,
      reason: selected ? 'Loading the payslip…' : 'Choose a payslip first',
    },
  ];

  const columns: Array<GridColumn<MyPayslipRow>> = [
    { key: 'period', header: 'Period', width: 160, render: (r) => periodText(r) },
    {
      key: 'run',
      header: 'Pay run',
      width: 220,
      render: (r) => (
        <span>
          <span className="mono">{r.documentNo}</span>
          {r.kind === 'OFF_CYCLE' && <span className="dim"> — {r.title ?? 'Off-cycle'}</span>}
        </span>
      ),
    },
    { key: 'gross', header: 'Gross', width: 120, align: 'end', render: (r) => money(r.grossPay) },
    { key: 'ded', header: 'Deductions', width: 120, align: 'end', render: (r) => money(r.deductionsTotal) },
    { key: 'net', header: 'Net pay', width: 120, align: 'end', render: (r) => <strong>{money(r.netPay)}</strong> },
    {
      key: 'paid',
      header: 'Paid on',
      width: 140,
      render: (r) => (r.paidOn ? shortDate(r.paidOn) : <StatePill state="UNPAID" />),
    },
  ];

  const tabs: DetailTab[] = selected
    ? [
        {
          id: 'payslip',
          label: 'Payslip',
          icon: 'payslip',
          content: d ? (
            <div className="payslip stack">
              <div className="payslip__who">
                <div>
                  <strong>{d.employeeName}</strong>{' '}
                  {d.employeeNameUr && (
                    <span className="urdu" lang="ur">
                      {d.employeeNameUr}
                    </span>
                  )}
                  <div className="dim">
                    <span className="mono">{d.employeeCode}</span>
                    {d.designationName ? ` · ${d.designationName}` : ''} · {d.farm}
                  </div>
                </div>
                <div className="payslip__period">
                  <strong>{periodText(d)}</strong>
                  <div className="dim">
                    {d.documentNo}
                    {d.title ? ` — ${d.title}` : ''}
                  </div>
                </div>
              </div>
              <dl className="detaillist">
                <dt>Days paid</dt>
                <dd>{dayCount(d.payableDays)}</dd>
                <dt>Absent</dt>
                <dd>{dayCount(d.absentDays)}</dd>
                <dt>Overtime</dt>
                <dd>{dayCount(d.overtimeHours)} hours</dd>
                <dt>Paid</dt>
                <dd>{d.paidOn ? `On ${shortDate(d.paidOn)}` : 'Not paid yet'}</dd>
              </dl>
              <div className="payslip__cols">
                <LinesTable
                  title="Earnings"
                  titleUr="آمدنی"
                  lines={d.lines.filter((l) => l.kind === 'EARNING').map((l) => ({ ...l, payAmount: l.rupees }))}
                  total={d.grossPay}
                  empty="No earnings."
                />
                <LinesTable
                  title="Deductions"
                  titleUr="کٹوتیاں"
                  lines={d.lines.filter((l) => l.kind === 'DEDUCTION').map((l) => ({ ...l, payAmount: l.rupees }))}
                  total={d.deductionsTotal}
                  empty="Nothing taken off."
                />
              </div>
              <NetPay net={d.netPay} />
            </div>
          ) : detail.isError ? (
            <ErrorNote message={detail.error.message} />
          ) : (
            <p className="muted">Loading the payslip…</p>
          ),
        },
      ]
    : [];

  return (
    <div className="workbench">
      {me && (
        <div className="payrun__head">
          <h2 className="withicon">
            <AppIcon kind="payslip" />
            <span>
              My payslips — {me.nameEn}{' '}
              {me.nameUr && (
                <span className="urdu" lang="ur">
                  {me.nameUr}
                </span>
              )}{' '}
              <span className="mono dim">{me.code}</span>
            </span>
          </h2>
          {summary.data?.owed && (
            <div className="mypay__summary">
              {summary.data.owed.length > 0 ? (
                summary.data.owed.map((a) => (
                  <span key={a.documentNo} className="pill">
                    {a.kind === 'LOAN' ? 'Loan' : 'Advance'} {a.documentNo}: {money(a.owedRupees)} still owed,{' '}
                    {money(a.eachMonthRupees)} a month
                  </span>
                ))
              ) : (
                <span className="dim">Nothing owed on advances or loans.</span>
              )}
              {(summary.data.leave ?? []).filter((l) => l.isPaid).length > 0 && (
                <span className="dim">
                  Leave left in {summary.data.year}:{' '}
                  {summary.data.leave
                    .filter((l) => l.isPaid)
                    .map((l) => `${l.nameEn} ${Number(l.remaining)}${Number(l.pending) > 0 ? ` (${Number(l.pending)} waiting)` : ''}`)
                    .join(' · ')}
                </span>
              )}
            </div>
          )}
        </div>
      )}
      <SplitView
        id="hr-mypay"
        direction="column"
        initial={320}
        firstLabel="payslip list"
        first={
          <div className="workbench__main">
            <ActionBar actions={actions} />
            {list.error && <ErrorNote message={list.error.message} />}
            <DataGrid
              id="hr-mypay"
              label="My payslips"
              columns={columns}
              rows={rows}
              rowKey={(r) => r.id}
              selectedKey={selectedKey}
              onSelect={(k) => setSelectedKey(k)}
              onOpen={(r) => {
                setSelectedKey(r.id);
                setTab('payslip');
              }}
              loading={list.isPending}
              empty="No payslips yet. They show here once a pay run is posted."
            />
          </div>
        }
        second={
          <DetailPane
            tabs={tabs}
            activeTab={tab}
            onTabChange={setTab}
            empty="Choose a payslip to read it."
            title={selected && <span>{periodText(selected)}</span>}
          />
        }
      />
      <StatusBar>
        <span>{rows.length} payslips</span>
        <span>{selected ? `Chosen: ${periodText(selected)}` : 'Nothing chosen'}</span>
      </StatusBar>
    </div>
  );
}
